import { TOKEN_ORIGIN, type TokenOrigin } from './token-origin.constants';

export function resolveEmbeddedToken(route: {
  snapshot: { queryParamMap: { get: (key: string) => string | null } };
}): string | null {
  const directToken = route.snapshot.queryParamMap.get('token');
  if (directToken) {
    return directToken;
  }

  const redirectTo = route.snapshot.queryParamMap.get('redirectTo');
  if (redirectTo) {
    try {
      const parsedRedirect = new URL(redirectTo, globalThis.location.origin);
      const tokenFromRedirect = parsedRedirect.searchParams.get('token');
      if (tokenFromRedirect) {
        return tokenFromRedirect;
      }
    } catch {
      // Ignore malformed redirectTo and continue with URL search fallback.
    }
  }

  return new URLSearchParams(globalThis.location.search).get('token');
}

/**
 * Decodes a JWT token and returns the payload as an object.
 * @param token - The JWT token string to decode
 * @returns The decoded token payload as an object, or null if invalid
 */
export function parseToken<T = Record<string, unknown>>(token: string): T | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    const decoded = base64UrlDecode(payload);
    return JSON.parse(decoded) as T;
  } catch {
    return null;
  }
}

/**
 * Determines the origin of a JWT token based on its audience claim.
 * Tokens whose aud claim includes "allplan" are identified as Allplan.
 * All other tokens default to Graphisoft.
 *
 * @param token - The raw JWT token string
 * @returns The token origin: 'allplan' or 'graphisoft'
 */
export function getTokenOrigin(token: string): TokenOrigin {
  const payload = parseToken<{ aud?: string | string[] }>(token);
  const aud = payload?.aud;

  if (Array.isArray(aud) ? aud.includes(TOKEN_ORIGIN.ALLPLAN) : aud === TOKEN_ORIGIN.ALLPLAN) {
    return TOKEN_ORIGIN.ALLPLAN;
  }

  return TOKEN_ORIGIN.GRAPHISOFT;
}

/**
 * Decodes a base64url string to UTF-8 text.
 * Handles missing padding and properly decodes UTF-8 characters.
 */
function base64UrlDecode(base64Url: string): string {
  // Convert base64url to base64
  let base64 = base64Url.replaceAll('-', '+').replaceAll('_', '/');

  // Add padding if needed (base64 length must be multiple of 4)
  const paddingNeeded = (4 - (base64.length % 4)) % 4;
  base64 += '='.repeat(paddingNeeded);

  // Decode base64 to binary string
  const binaryString = atob(base64);

  // Convert binary string to Uint8Array (each char is a single byte from atob)
  const bytes = Uint8Array.from(binaryString, char => char.codePointAt(0)!);

  // Decode UTF-8 bytes to string
  return new TextDecoder().decode(bytes);
}
